import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { getStartSessionTimestamp, Session } from '@mindly/ui-components';
import { useIonViewDidLeave } from '@ionic/react';
import { ConsultationType } from '../../pages/types';
import { consultationsSortedByTimeSelector } from '../../redux/consultations/selector';
import { globalVariables } from '../../redux/firestoreGlobalVariables/selector';
import { userSelector } from '../../redux/user/selector';
import {
  setSessionForReview,
  setTrialSessionReviewModalIsOpen,
} from '../../redux/consultationReviews/consultationsReviews.reducer';
import {
  getReviewedSessions,
  mergeSession,
  closeAllSessions,
  getTrialSessionReviewPromoCode,
  getTrialSessionReviewContinueTherapy,
} from '../utils/manageTrialSessionReview';
import { getReviewedSessions as getReviewedSubscriptionSessions } from '../utils/manageSubscriptionSessionReview';
import { usePushNotifications } from '../context/PushNotificationsContext';

const minHours = 0.5;
const refreshInterval = 1.35 * 1000;

export const useTrialSessionReviewTimer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pushNotificationsState = usePushNotifications();
  const clientRef = useRef<any>();
  const intervalRef = useRef<any>();
  const globalVars = useSelector(globalVariables);
  const featureStartDate = globalVars?.features?.session_reviews?.start_date?._seconds
    ? DateTime.fromMillis(globalVars?.features?.session_reviews?.start_date._seconds * 1000)
    : DateTime.utc(2025, 6, 1, 9, 0, 0);

  const client = useSelector(userSelector);
  clientRef.current = client;
  const consultations = useSelector(consultationsSortedByTimeSelector);
  const [localConsultations, setLocalConsultations] = useState<(ConsultationType | Session)[]>([]);

  useEffect(() => {
    if (client?.id && consultations.length > 0) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
      setLocalConsultations(consultations.filter((con) => con.payment_intent_id && !con.session_review));
    }
  }, [consultations, client]);

  useEffect(() => {
    if (localConsultations.length > 0 && clientRef.current?.id) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }

      intervalRef.current = setInterval(() => {
        if (!clientRef.current?.id) {
          clearInterval(intervalRef.current);
          intervalRef.current = undefined;
          closeAllSessions();
          return;
        }

        const isPromoCodeModalOpen = getTrialSessionReviewPromoCode();
        const isContinueTherapyOpened = getTrialSessionReviewContinueTherapy();

        if (isPromoCodeModalOpen || isContinueTherapyOpened) {
          return;
        }

        const urlName = 'video-call';
        const videoIsActive =
          document?.location?.href?.includes(urlName) ||
          document?.location?.pathname?.includes(urlName) ||
          window?.location?.pathname?.includes(urlName) ||
          location?.pathname?.includes(urlName);

        if (videoIsActive || pushNotificationsState.isOpenPushNotificationModal) {
          return;
        }

        const reviewedSessions = getReviewedSessions();
        const reviewedSubscriptionSessions = getReviewedSubscriptionSessions();

        const hasOpenSession = reviewedSessions.some((session) => session.isOpen);
        const hasOpenSubscriptionSession = reviewedSubscriptionSessions.some((session) => session.isOpen);
        if (hasOpenSession || hasOpenSubscriptionSession) {
          return;
        }

        const sessionsToReview = localConsultations.filter((con: ConsultationType | Session) => {
          const reviewedSession = reviewedSessions.find((reviewed) => reviewed.sessionId === con.id);

          if (reviewedSession?.hideForever) {
            return false;
          }

          if (reviewedSession?.userId && reviewedSession.userId !== clientRef.current?.id) {
            return false;
          }

          const sessionStartTime = getStartSessionTimestamp(con.consultation_time);
          if (DateTime.fromMillis(sessionStartTime).diff(featureStartDate, 'hours').hours < 0) {
            return false;
          }

          const consultationTime =
            typeof con.consultation_time === 'string'
              ? DateTime.fromISO(con.consultation_time)
              : DateTime.fromSeconds(con.consultation_time._seconds);

          const timeDiff = DateTime.local().diff(consultationTime, 'hours').hours;

          return minHours <= timeDiff;
        });

        if (sessionsToReview.length > 0) {
          const sessionToReview = sessionsToReview[0];
          const consultationTime =
            typeof sessionToReview.consultation_time === 'string'
              ? DateTime.fromISO(sessionToReview.consultation_time)
              : DateTime.fromSeconds(sessionToReview.consultation_time._seconds);

          dispatch(setSessionForReview(sessionToReview));
          dispatch(setTrialSessionReviewModalIsOpen(true));

          mergeSession(sessionToReview.id, {
            sessionStartTimestamp: consultationTime.toMillis(),
            hideForever: false,
            isOpen: true,
            userId: client?.id ?? '',
          });
        }
      }, refreshInterval);
    }
  }, [localConsultations, pushNotificationsState.isOpenPushNotificationModal, client?.id]);

  useIonViewDidLeave(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
      setLocalConsultations([]);
      closeAllSessions();
    }
  });

  return () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
      setLocalConsultations([]);
      closeAllSessions();
    }
  };
};
